import { isFunction } from "lodash";

import type { DOMRefValue, FocusableRefValue } from "@react-types/shared";
import type { ReactElement, RefObject } from "react";
import type { ReactRef } from "./types";

export const createDOMRef = <T extends HTMLElement = HTMLElement>(ref: RefObject<T | null>) =>
  ({
    UNSAFE_getDOMNode() {
      return ref.current;
    },
  }) as DOMRefValue<T>;

export const createFocusableRef = <T extends HTMLElement = HTMLElement>(
  domRef: RefObject<T | null>,
  focusableRef: RefObject<HTMLElement | null> = domRef
): FocusableRefValue<T> => ({
  ...createDOMRef(domRef),
  focus() {
    if (focusableRef.current) {
      focusableRef.current.focus();
    }
  },
});

/**
 * Assigns a value to a ref function or object
 *
 * @param ref the ref to assign to
 * @param value the value
 */
export function assignRef<T = unknown>(ref: ReactRef<T> | undefined, value: T) {
  if (ref == null) return;

  if (isFunction(ref)) {
    ref(value);

    return;
  }

  try {
    ref.current = value;
  } catch (error) {
    throw new Error(`Cannot assign value '${value}' to ref '${ref}'`);
  }
}

/**
 * Combine multiple React refs into a single ref function.
 * This is used mostly when you need to allow consumers forward refs to
 * internal components
 *
 * @param refs refs to assign to value to
 */
export function mergeRefs<T>(...refs: (ReactRef<T> | undefined)[]) {
  return (node: T | null) => {
    refs.forEach(ref => assignRef(ref, node));
  };
}

export function getElementRef<T = unknown>(element: ReactElement): ReactRef<T> | undefined {
  const propsRef = (element.props as { ref?: ReactRef<T> }).ref;
  if (propsRef !== undefined) return propsRef;

  return Object.getOwnPropertyDescriptor(element, "ref")?.value as ReactRef<T> | undefined;
}
